import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaCalendarAlt, FaPlus, FaEdit, FaTrash, FaSearch, FaFilter, FaChevronDown, FaCheck, FaTimesCircle, FaCalendarDay } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import { isToday } from '../../utils/helpers';

/**
 * Session management component for mentors
 * Allows scheduling, viewing, and managing mentoring sessions
 */
const SessionManagement = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState('upcoming');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [sessionToCancel, setSessionToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const { showSuccess, showError } = useToast();
  
  // Fetch sessions on component mount and when filters change
  useEffect(() => {
    fetchSessions();
  }, [statusFilter, typeFilter, dateRangeFilter]);
  
  // Fetch sessions from API
  const fetchSessions = async () => {
    try {
      setLoading(true);
      
      // Build query params based on filters
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('type', typeFilter);
      if (dateRangeFilter) params.append('dateRange', dateRangeFilter);
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/mentors/sessions${queryString}`);
      setSessions(response.data.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      showError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle session cancellation
  const handleCancelClick = (session) => {
    setSessionToCancel(session);
    setCancelReason('');
    setCancelModalOpen(true);
  };
  
  // Process session cancellation
  const handleCancelConfirm = async () => {
    if (!sessionToCancel || !cancelReason.trim()) return;
    
    try {
      await api.put(`/mentors/sessions/${sessionToCancel._id}/cancel`, {
        cancelReason,
        canceledBy: 'mentor'
      });
      
      showSuccess('Session cancelled successfully');
      fetchSessions();
      setCancelModalOpen(false);
    } catch (error) {
      console.error('Error cancelling session:', error);
      showError(error.response?.data?.message || 'Error cancelling session');
    }
  };
  
  // Format date-time
  const formatDateTime = (dateString) => {
    const options = { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Filter sessions based on search term
  const filteredSessions = sessions.filter((session) => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    return (
      `${session.child.firstName} ${session.child.lastName}`.toLowerCase().includes(search) ||
      `${session.parent.firstName} ${session.parent.lastName}`.toLowerCase().includes(search) ||
      (session.topics && session.topics.some(topic => topic.toLowerCase().includes(search)))
    );
  });
  
  // Get status badge styling
  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'canceled':
        return 'bg-red-100 text-red-800';
      case 'rescheduled':
        return 'bg-yellow-100 text-yellow-800';
      case 'no_show':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Format session type for display
  const formatSessionType = (type) => {
    switch(type) {
      case 'video_call':
        return 'Video Call';
      case 'in_person':
        return 'In Person';
      case 'phone_call':
        return 'Phone Call';
      case 'chat':
        return 'Chat';
      default:
        return type;
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaCalendarAlt className="mr-2 text-primary" /> Session Management
          </h1>
          <p className="text-gray-600 mt-1">
            Schedule and manage your mentoring sessions
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <Link
            to="/mentor/sessions/new"
            className="btn-primary flex items-center"
          >
            <FaPlus className="mr-2" /> Schedule Session
          </Link>
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-soft p-4 mb-6">
        <div className="flex flex-col space-y-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              className="form-input pl-10 py-2 w-full"
              placeholder="Search sessions by student name or topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div>
            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className="flex items-center text-sm text-gray-600 hover:text-primary"
            >
              <FaFilter className="mr-1" />
              Filters
              <FaChevronDown className={`ml-1 transform transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {filtersOpen && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                <div>
                  <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    id="statusFilter"
                    className="form-input"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="completed">Completed</option>
                    <option value="canceled">Canceled</option>
                    <option value="rescheduled">Rescheduled</option>
                    <option value="no_show">No Show</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="typeFilter" className="block text-sm font-medium text-gray-700 mb-1">
                    Session Type
                  </label>
                  <select
                    id="typeFilter"
                    className="form-input"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="video_call">Video Call</option>
                    <option value="in_person">In Person</option>
                    <option value="phone_call">Phone Call</option>
                    <option value="chat">Chat</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="dateRangeFilter" className="block text-sm font-medium text-gray-700 mb-1">
                    Date Range
                  </label>
                  <select
                    id="dateRangeFilter"
                    className="form-input"
                    value={dateRangeFilter}
                    onChange={(e) => setDateRangeFilter(e.target.value)}
                  >
                    <option value="upcoming">Upcoming</option>
                    <option value="past">Past</option>
                    <option value="today">Today</option>
                    <option value="thisWeek">This Week</option>
                    <option value="thisMonth">This Month</option>
                    <option value="all">All Time</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Sessions Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-6 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft overflow-hidden">
          {filteredSessions.length === 0 ? (
            <div className="p-6 text-center">
              <FaCalendarDay className="mx-auto text-gray-300 text-4xl mb-3" />
              <p className="text-gray-500">No sessions found</p>
              <Link 
                to="/mentor/sessions/new" 
                className="mt-3 inline-block px-4 py-2 bg-primary text-white rounded-md text-sm font-medium"
              >
                Schedule New Session
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSessions.map((session) => (
                    <tr 
                      key={session._id} 
                      className={isToday(session.scheduledDate) && session.status === 'scheduled' ? 'bg-blue-50' : ''}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center mr-3">
                            {session.child.firstName?.charAt(0)}
                            {session.child.lastName?.charAt(0)}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {session.child.firstName} {session.child.lastName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {session.parent.firstName} {session.parent.lastName} (Parent)
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${isToday(session.scheduledDate) && session.status === 'scheduled' ? 'font-semibold text-primary' : 'text-gray-900'}`}>
                          {formatDateTime(session.scheduledDate)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {session.duration} minutes
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {formatSessionType(session.sessionType)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(session.status)}`}>
                          {session.status.charAt(0).toUpperCase() + session.status.slice(1).replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link 
                          to={`/mentor/sessions/${session._id}`} 
                          className="text-primary hover:text-primary-600 mr-3"
                          title="View Details"
                        >
                          View
                        </Link>
                        
                        {session.status === 'scheduled' && (
                          <>
                            <Link 
                              to={`/mentor/sessions/${session._id}/edit`} 
                              className="text-blue-500 hover:text-blue-700 mr-3"
                              title="Edit Session"
                            >
                              <FaEdit className="inline" />
                            </Link>
                            <button
                              onClick={() => handleCancelClick(session)}
                              className="text-red-500 hover:text-red-700"
                              title="Cancel Session"
                            >
                              <FaTimesCircle className="inline" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Cancel Session Modal */}
      <Modal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        title="Cancel Session"
        size="md"
      >
        <div className="p-6">
          <p className="text-gray-700 mb-4">
            Are you sure you want to cancel your session with 
            <strong> {sessionToCancel?.child?.firstName} {sessionToCancel?.child?.lastName}</strong> scheduled for 
            <strong> {sessionToCancel ? formatDateTime(sessionToCancel.scheduledDate) : ''}</strong>?
          </p>
          
          <div className="mb-4">
            <label htmlFor="cancelReason" className="form-label">
              Reason for Cancellation
            </label>
            <textarea
              id="cancelReason"
              rows="3"
              className="form-input"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Please provide a reason for cancelling this session"
            ></textarea>
            {!cancelReason.trim() && (
              <p className="text-red-500 text-sm mt-1">Reason is required</p>
            )}
          </div>
          
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setCancelModalOpen(false)}
              className="btn-outline mr-2"
            >
              Keep Session
            </button>
            <button
              type="button"
              onClick={handleCancelConfirm}
              disabled={!cancelReason.trim()}
              className={`bg-red-500 hover:bg-red-700 text-white font-medium py-2 px-4 rounded ${!cancelReason.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Cancel Session
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SessionManagement;
